/**
 * Platform-agnostic interface for a "pool" audio player that can load TTS
 * audio chunks (one per HTTP fetch), play them with pitch-preserved rate
 * control, and prewarm the next chunk so chunk-to-chunk transitions are
 * instant.
 *
 * Web implementation: a pool of HTMLAudioElements with LRU eviction.
 *   apps/start/src/lib/audio-chunk-player/player.tsx
 *
 * Native (RN) implementation: expo-audio AudioPlayer instances managed the
 * same way.
 *   apps/mobile/src/lib/audio-pool-player-expo.ts
 *
 * The chunk-aware state machine (auto-advance, prefetch N chunks, seek across
 * chunks) sits on top of this interface and is itself platform-agnostic.
 */

export type AudioPoolControls = {
  volume: number;
  muted: boolean;
  rate: number;
};

export type AudioPoolError = {
  /** Stable error code for diagnostics. Adapter-defined. */
  code: string;
  message: string;
};

export type AudioPoolPrewarmOptions = {
  onCanPlay?: () => void;
  onError?: (error: AudioPoolError) => void;
  playbackLogMetadata?: AudioPoolPlaybackLogMetadata;
};

export type AudioPoolPlaybackLogMetadata = {
  audioId: string;
  chapterId: string;
  chapterTitle?: string;
  ttsId: string;
  playbackMode: 'legacy' | 'doc-pack';
  docPackChunkId?: string;
};

/**
 * OS "Now Playing" / lock-screen / notification metadata for the chunk being
 * loaded. Native adapters (expo-audio) forward this to the platform media
 * session so playback shows on the lock screen + Control Center. The web
 * adapter ignores it. `title` = chapter title, `artist` = document title,
 * `albumTitle` = "Listening". With the duration override set (the patched
 * expo-audio fields), the OS scrubber spans the whole document; without it,
 * the current chunk.
 */
export type AudioPoolNowPlayingMetadata = {
  title?: string;
  artist?: string;
  albumTitle?: string;
  /** Must be an http(s) URL — native artwork is fetched over the network. */
  artworkUrl?: string;
  /** Doc-wide duration the OS should show instead of the chunk's. While set,
   * lock-screen seek/skip commands arrive doc-absolute through
   * `setLockScreenCommandHandler` instead of seeking the chunk. */
  durationSec?: number;
  /** OS elapsed = this + the chunk's live position. */
  elapsedOffsetSec?: number;
  useChunkTimeline?: boolean;
};

/** Lock-screen command native routes to JS while a duration override is
 * armed. `seekTo.positionMs` is doc-absolute; `skip.offsetMs` is signed. */
export type AudioPoolLockScreenCommand =
  | { type: 'play' | 'pause' }
  | { type: 'seekTo'; positionMs: number }
  | { type: 'skip'; offsetMs: number };

export type AudioPoolLoadOptions = Partial<AudioPoolControls> &
  AudioPoolPrewarmOptions & {
    onEnded?: () => void;
    /** 'advance' marks the boundary load that follows onEnded, letting an
     * adapter whose transport already moved on reconcile forward instead of
     * seeking. Explicit jumps (seek, initial load) omit it. */
    cause?: 'advance';
    /** OS Now Playing metadata for this chunk. Native-only; web ignores it. */
    metadata?: AudioPoolNowPlayingMetadata;
  } & (
    | { autoplay: true; onPlay?: () => void }
    | { autoplay?: false; onPlay?: undefined }
  );

export type AudioPoolSnapshot = Readonly<AudioPoolControls> & {
  readonly duration: number;
  readonly src: string;
  readonly error: AudioPoolError | null;

  readonly poolSize: number;
  readonly poolCount: number;
  /** Monotonic counter bumped on every `seek()`. Position is deliberately kept
   * OUT of the snapshot (it would re-render every tick), but a paused
   * within-chunk seek changes nothing else observable — this lets derived
   * consumers (`usePlaybackDerived`) resample after a paused seek. Adapters
   * mounted through `AudioPoolPlayerInstanceProvider` need not track it — the
   * provider counts seeks itself and overrides this field on the handle. */
  readonly seekCount?: number;
} & (
    | { readonly isPlaying: true; readonly isPaused: false }
    | { readonly isPlaying: false; readonly isPaused: true }
  ) &
  (
    | { readonly isError: false; readonly error: null }
    | { readonly isError: true; readonly error: AudioPoolError }
  ) &
  (
    | { readonly isReady: true; readonly isLoading: false }
    | { readonly isReady: false; readonly isLoading: true }
    | { readonly isReady: false; readonly isLoading: false }
  );

export const defaultAudioPoolSnapshot: AudioPoolSnapshot = Object.freeze({
  volume: 1,
  muted: false,
  rate: 1,

  duration: 0,
  src: '',
  error: null,

  poolSize: 2,
  poolCount: 0,
  seekCount: 0,

  isLoading: false,
  isReady: false,
  isError: false,
  isPlaying: false,
  isPaused: true,
});

export type AudioPoolPlayer = {
  /** useSyncExternalStore-shaped subscription. */
  subscribe: (cb: () => void) => () => void;
  getSnapshot: () => AudioPoolSnapshot;

  load: (src: string, options?: AudioPoolLoadOptions) => void;
  prewarmLoad: (src: string, options?: AudioPoolPrewarmOptions) => void;

  play: () => Promise<void>;
  pause: () => void;
  togglePlayPause: () => Promise<void>;

  stop: () => void;
  seek: (seconds: number) => void;
  getPositionSec: () => number;
  getPositionMs: () => number;

  setRate: (rate: number) => void;
  setVolume: (vol: number) => void;
  mute: () => void;
  unmute: () => void;

  /** Merge doc-scoped overrides into the armed lock-screen metadata and push
   * them in place. Native-only; adapters without a lock screen omit it. */
  updateNowPlaying?: (
    overrides: Pick<
      AudioPoolNowPlayingMetadata,
      'durationSec' | 'elapsedOffsetSec' | 'useChunkTimeline'
    >,
  ) => void;
  /** Single handler for lock-screen commands routed to JS while a duration
   * override is armed. `null` unregisters. */
  setLockScreenCommandHandler?: (
    handler: ((command: AudioPoolLockScreenCommand) => void) | null,
  ) => void;

  destroy: () => void;
};
